import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { useKivunovaTranslation } from "@kivunova/kivufrontendcommon";
import CommonLoader from "../../common/CommonLoader/CommonLoader";
import AppButton from "../../common/AppButton";
import StockRequisitionForm from "./StockRequisitionForm";
import {
  useApproveStockRequisition,
  useCancelStockRequisition,
  useCreateStockRequisition,
  useIssueStockRequisition,
  useStockRequisitions,
  useSubmitStockRequisition
} from "../../../hooks/api/education/useStockRequisition";

const StockUsagePage: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const [openForm, setOpenForm] = useState(false);
  const {
    data: requisitions = [],
    isPending,
    refetch
  } = useStockRequisitions({ size: 50 });
  const createMutation = useCreateStockRequisition();
  const submitMutation = useSubmitStockRequisition();
  const approveMutation = useApproveStockRequisition();
  const issueMutation = useIssueStockRequisition();
  const cancelMutation = useCancelStockRequisition();

  if (isPending) {
    return (
      <CommonLoader
        loadingMessage={t(
          "loading-stock-requisitions",
          "Loading stock usage requests..."
        )}
      />
    );
  }

  const runAction = async (
    id: string,
    action: "submit" | "approve" | "issue" | "cancel"
  ) => {
    const input = { requisitionId: id };
    if (action === "submit") await submitMutation.mutateAsync(input);
    if (action === "approve") await approveMutation.mutateAsync(input);
    if (action === "issue") await issueMutation.mutateAsync(input);
    if (action === "cancel") await cancelMutation.mutateAsync(input);
    refetch();
  };

  return (
    <Box data-testid="stock-usage-page" sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h5" component="h1">
          {t("stock-usage-title", "Stock usage")}
        </Typography>
        <AppButton onClick={() => setOpenForm(true)}>
          {t("new-stock-request", "New request")}
        </AppButton>
      </Box>

      <StockRequisitionForm
        open={openForm}
        onClose={() => setOpenForm(false)}
        loading={createMutation.isPending}
        onSubmit={async (input) => {
          await createMutation.mutateAsync(input);
          setOpenForm(false);
          refetch();
        }}
      />

      {requisitions.length === 0 ? (
        <Typography color="text.secondary">
          {t("stock-requisitions-empty", "No stock usage requests yet.")}
        </Typography>
      ) : (
        <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none" }}>
          {requisitions.map((req) => (
            <Box
              component="li"
              key={req.id}
              sx={{
                py: 1.5,
                borderBottom: "1px solid",
                borderColor: "divider"
              }}
            >
              <Typography fontWeight={600}>
                {req.orderNumber} · {req.requisitionStatus}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {req.purpose || t("stock-request-no-purpose", "No purpose")}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
                {req.requisitionStatus === "DRAFT" && (
                  <AppButton
                    size="small"
                    onClick={() => runAction(req.id!, "submit")}
                  >
                    {t("submit", "Submit")}
                  </AppButton>
                )}
                {req.requisitionStatus === "SUBMITTED" && (
                  <AppButton
                    size="small"
                    onClick={() => runAction(req.id!, "approve")}
                  >
                    {t("approve", "Approve")}
                  </AppButton>
                )}
                {req.requisitionStatus === "APPROVED" && (
                  <AppButton
                    size="small"
                    onClick={() => runAction(req.id!, "issue")}
                  >
                    {t("issue", "Issue")}
                  </AppButton>
                )}
                {req.requisitionStatus !== "ISSUED" &&
                  req.requisitionStatus !== "CANCELLED" && (
                    <AppButton
                      size="small"
                      variant="outlined"
                      onClick={() => runAction(req.id!, "cancel")}
                    >
                      {t("cancel", "Cancel")}
                    </AppButton>
                  )}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default StockUsagePage;
